













































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Question, Response } from '@/store/modules/survey-store-module'
import AutomatedInsights, { AutomatedInsightObj, AutomatedInsightVariantObj } from './AutomatedInsights.vue'
import * as db from '@shared/types/database'
import { Card, Icon, Select } from '@alpha/ink'

import {
  MatrixQuestion,
  MultiSelectQuestion,
  NumberQuestion,
  RankQuestion,
  RankDistributedQuestion,
  RateQuestion,
  SingleSelectQuestion,
  DescriptionQuestion,
  VariantQuestion,
  Sentiment,
  Wordcloud
} from './QuestionTypes/index'
import Tabs from '@/components/Tabs/Tabs.vue'
import store from '@/store'
import loadingGif from '@/assets/img/loading.gif'
import { GLOBAL_CHART_HEIGHT, SliceOption, TextAreaChartType, RankChartType } from './Utilities/ChartUtilities'
import QuestionTitleEditor from './QuestionTitleEditor.vue'
import QuestionContext from './QuestionContext.vue'
import { removeHiddenResponses, parentQuestions, respondentCountText } from './Utilities/SurveyUtilities'
import QuestionDropDownOptions from './QuestionDropDownOptions.vue'
import { sharedViewRouteName } from '@/router'
import SurveyQuestionAttachment from './SurveyQuestionAttachment.vue'
import { TabData } from '@/components/Tabs/TabTypes'
import { QuestionAttachment } from '@/components/Survey/SurveyQuestionAttachment.vue'

@Component({
  components: {
    AutomatedInsights,
    SingleSelectQuestion,
    MultiSelectQuestion,
    NumberQuestion,
    RankQuestion,
    RankDistributedQuestion,
    RateQuestion,
    MatrixQuestion,
    VariantQuestion,
    DescriptionQuestion,
    Sentiment,
    Wordcloud,
    Card,
    Icon,
    Select,
    Tabs,
    QuestionTitleEditor,
    QuestionContext,
    QuestionDropDownOptions,
    SurveyQuestionAttachment
  }
})
export default class SurveyQuestion extends Vue {
  @Prop({ required: true }) question!: Question & { responses: Response[] }

  // This is passed down by the "global" select slice, which will set it for all the questions. It does not take precedence,
  // it can be overridden by the question level sliceOption in this component
  @Prop({ required: true }) globalSliceBy!: SliceOption

  // All the select options, pass it as prop so the individual questions don't need to know about all the other questions
  // for slicing by question
  @Prop({ required: true }) sliceOptions!: SliceOption[]

  filteredSliceOptions: SliceOption[] = this.sliceOptions.filter(option => option.value !== this.question.id)
  selectedSliceOption: SliceOption = this.filteredSliceOptions[0]
  selectedChartType: string | null = this.chartOptions[0]?.title || null
  GLOBAL_CHART_HEIGHT: string = GLOBAL_CHART_HEIGHT
  loadingGif = loadingGif
  isTitleEditMode: boolean = false

  get chartOptions (): TabData[] {
    if (this.question.type === db.QuestionType.TextArea) {
      return [
        { title: TextAreaChartType.Frequency },
        { title: TextAreaChartType.Sentiment }
      ]
    } else if (this.question.type === db.QuestionType.Rank) {
      return [
        { title: RankChartType.Weighted },
        { title: RankChartType.Distribution }
      ]
    } else return []
  }

  get hasChartOptions () {
    return this.isTextArea || this.question.type === db.QuestionType.Rank
  }

  get publicView () {
    return this.$route.name === sharedViewRouteName
  }

  get questionTitle () {
    return this.questionConfig.title || this.question.text
  }

  get chartComponentForQuestionType () {
    const typeMap: Record<string, string> = {
      [db.QuestionType.SingleSelect]: 'SingleSelectQuestion',
      [db.QuestionType.MultiSelect]: 'MultiSelectQuestion',
      [db.QuestionType.Rate]: 'RateQuestion',
      [db.QuestionType.RateOnly]: 'RateQuestion',
      [db.QuestionType.Number]: 'NumberQuestion',
      [db.QuestionType.Matrix]: 'MatrixQuestion',
      [db.QuestionType.Variant]: 'VariantQuestion',
      [db.QuestionType.Description]: 'DescriptionQuestion',
      [db.QuestionType.TextArea]: 'TextAreaQuestion'
    }

    if (this.question.type === db.QuestionType.TextArea) {
      return this.selectedChartType === TextAreaChartType.Frequency ? 'Wordcloud' : 'Sentiment'
    } else if (this.question.type === db.QuestionType.Rank) {
      return this.selectedChartType === RankChartType.Weighted ? 'RankQuestion' : 'RankDistributedQuestion'
    } else {
      return typeMap[this.question.type]
    }
  }

  get getDisplayQuestionType () {
    const typeMap: Record<string, string> = {
      [db.QuestionType.SingleSelect]: db.QuestionType.SingleSelect,
      [db.QuestionType.MultiSelect]: db.QuestionType.MultiSelect,
      [db.QuestionType.Rank]: db.QuestionType.Rank,
      [db.QuestionType.Rate]: 'Rate + Explanation',
      [db.QuestionType.RateOnly]: db.QuestionType.Rate,
      [db.QuestionType.Number]: db.QuestionType.Number,
      [db.QuestionType.Matrix]: db.QuestionType.Matrix,
      [db.QuestionType.TextArea]: 'Open End',
      [db.QuestionType.Variant]: db.QuestionType.Variant,
      [db.QuestionType.Description]: db.QuestionType.Description
    }

    return typeMap[this.question.type]
  }

  get questionHasChart () {
    return this.question.type !== db.QuestionType.Variant &&
      this.question.type !== db.QuestionType.Description
  }

  @Watch('globalSliceBy', { immediate: true })
  globalSliceChanged () {
    this.sliceTypeSelected(this.globalSliceBy)
  }

  @Watch('isSliceableQuestion')
  resetSliceType () {
    this.selectedSliceOption = this.filteredSliceOptions[0]
  }

  public sliceTypeSelected (selectedOption: SliceOption) {
    if (selectedOption && this.isSliceableQuestion) {
      if (selectedOption.value === this.question.id) { // when sliced by self, revert to default slice
        this.resetSliceType()
        return
      }
      this.selectedSliceOption = selectedOption
    }
  }

  revealInsight (slice: db.SliceType) {
    if (typeof slice !== 'string') {
      this.resetSliceType()
      return
    }
    this.sliceTypeSelected({
      value: slice,
      label: slice
    })
  }

  get isTextArea (): boolean {
    return this.question.type === db.QuestionType.TextArea
  }

  get isSliceableQuestion () {
    const isVariantDescription = this.question.type === db.QuestionType.Variant
    const isDescription = this.question.type === db.QuestionType.Description
    const isVariantMatrix = this.question.type === db.QuestionType.Matrix && this.isVariant
    const isVariantWordCloud = this.isTextArea && this.isVariant && this.selectedChartType === TextAreaChartType.Frequency
    const isVariantRankDistribution = this.question.type === db.QuestionType.Rank && this.isVariant && this.selectedChartType === RankChartType.Distribution

    return !isDescription && !isVariantDescription && !isVariantMatrix && !isVariantWordCloud && !isVariantRankDistribution
  }

  get isSliced (): boolean {
    return (this.selectedSliceOption.value !== db.SliceType.DEFAULT && this.selectedSliceOption.value !== this.question.id)
  }

  get isVariant (): boolean {
    return !!this.question.variantRelationId
  }

  get indexResponses (): Response[] {
    return this.question.responses
  }

  get filteredIndexResponses (): Response[] {
    return removeHiddenResponses(this.question.responses)
  }

  get slicedResponses () {
    return this.sliceResponses(true)
  }

  get filteredSlicedResponses () {
    return this.sliceResponses(false)
  }

  get showQuestionContext () {
    return (this.questionConfig && this.questionConfig.title) || this.question.parentQuestionIds?.length || this.filteredIndexResponses.length !== this.indexResponses.length
  }

  sliceResponses (includeHiddenResponses: boolean) {
    const responses = includeHiddenResponses ? this.indexResponses : this.filteredIndexResponses
    return store.getters.survey.sliceResponses(responses, this.question, this.selectedSliceOption, this.isSliced, this.isSliceableQuestion)
  }

  get parentQuestions () {
    return parentQuestions(this.question)
  }

  get respondentCountText () {
    return respondentCountText(this.question, this.parentQuestions, this.filteredIndexResponses, this.indexResponses)
  }

  get questionText () {
    return this.question.text.replace(/<br\s*\/?>/gi, ' ') // replaces <br> with space
  }

  /// //////// Insights ///////////////
  get questionInsights ():AutomatedInsightObj[] {
    return store.getters.survey.insights
      .filter(i => i.type === 'Main' && i.questionId === this.question.id)
      .map(i => ({
        ...i,
        question: this.question
      })) || []
  }

  get questionDemographicInsights ():AutomatedInsightObj[] {
    return store.getters.survey.insights
      .filter(i => i.type === 'Demographic' && i.questionId === this.question.id)
      .map(i => ({
        ...i,
        question: this.question
      })) || []
  }

  get questionVariantInsights ():AutomatedInsightVariantObj[] {
    const variantQuestion = store.getters.survey.questionById(this.question.variantRelationId!)
    return store.getters.survey.insights
      .filter(i => i.type === 'Variant' && i.questionId === this.question.id)
      .map(i => ({
        ...i,
        variantOptions: variantQuestion?.options || []
      })) || []
  }

  /// //////// Config ///////////////
  get questionConfig () {
    return store.getters.surveyConfiguration.questionConfigById(this.question.id)
  }

  setTitleEditMode (isOn: boolean) {
    this.isTitleEditMode = isOn
  }

  get attachmentsForQuestion (): QuestionAttachment[] {
    const variantQuestion = store.state.survey.questions.find(question => question.id === this.question.variantRelationId)
    const questionAttachments = this.question.qualifyingTags.reduce((acc: QuestionAttachment[], curr) => {
      const asset = variantQuestion?.options?.find(opt => {
        return opt.qualificationTags?.includes(curr)
      })
      if (asset?.image) {
        acc.push({ label: asset.label, url: asset.image })
      }
      return acc
    }, [])

    for (const asset of this.question.assets ?? []) {
      questionAttachments.push({ label: asset.title, url: asset.url })
    }

    return questionAttachments
  }

  resetDemographicsSlice (selected:string) {
    if (selected === RankChartType.Distribution && this.isVariant) {
      this.resetSliceType()
    }
  }
}
